import React, { ReactNode, SyntheticEvent, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../../../../constants'
import SegmentPreview from '../../shared/components/segment-preview'
import { ICapture, ISegment } from '../../shared/types'
import CellMenu from './cell-menu'
import { CellOverlay, DimGradient, DurationPill, EditButton, SelectButton } from './elements'

const Menu = styled(CellMenu)``

const Cell = styled.div`
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    position: relative;
    user-select: none;

    ${DimGradient}, ${EditButton}, ${SelectButton}, ${Menu} {
        opacity: 0;
    }

    &:hover {
        ${DimGradient}, ${EditButton}, ${SelectButton}, ${Menu} {
            opacity: 1;
        }
    }
`

const CellPills = styled.div`
    bottom: 10px;
    left: 10px;
    position: absolute;
`

const Preview = styled(SegmentPreview)`
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
`

type Props = {
    children?: ReactNode
    capture: ICapture
    segment: ISegment
    height: number
    width: number
    isSelected?: boolean
    isPaused?: boolean
    isSoundOn?: boolean
    onSelect: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
    onEdit: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
    onDuplicate: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
    onDelete: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
    onRevert: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
    onMergeHover?: (direction?: 'left' | 'right') => void
    onMergeRight?: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
    onMergeLeft?: (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => void
}

const SuperCutGridCell = ({
    children,
    capture,
    segment,
    height,
    width,
    isSelected = false,
    isPaused = false,
    isSoundOn = false,
    onSelect,
    onEdit,
    onDuplicate,
    onDelete,
    onRevert,
    onMergeHover,
    onMergeRight,
    onMergeLeft,
}: Props) => {
    const [isHovered, setIsHovered] = useState<boolean>(false)
    const [ref, isInView] = useInView()
    const duration = segment.editParams?.trim
        ? segment.editParams.trim.end - segment.editParams.trim.start
        : segment.duration

    return (
        <Cell
            ref={ref}
            {...{
                style: { width, height },
                onMouseOver: () => setIsHovered(true),
                onMouseOut: () => setIsHovered(false),
            }}
        >
            <Preview
                {...{
                    capture,
                    segment,
                    width,
                    height,
                    showVideo: isHovered && isSoundOn,
                    soundEnabled: isSoundOn,
                    isPaused: isPaused || !isInView,
                }}
            />
            <CellOverlay selected={isSelected}>
                <DimGradient onClick={onSelect} />
                <EditButton onClick={onEdit}>
                    <i className="ss-write" />
                </EditButton>
                <SelectButton
                    {...{
                        selected: isSelected,
                        style: isSelected ? { opacity: 1 } : {},
                        onClick: onSelect,
                    }}
                >
                    <i className="ss-check" />
                </SelectButton>
                <Menu
                    {...{
                        segment,
                        onMergeHover,
                        onDelete,
                        onDuplicate,
                        onRevert,
                        onMergeRight,
                        onMergeLeft,
                    }}
                />
                <CellPills>
                    <DurationPill>{duration.toFixed(1)}s</DurationPill>
                    {segment.editParams?.isMerged && <DurationPill>Merged</DurationPill>}
                </CellPills>
            </CellOverlay>
            {children}
        </Cell>
    )
}

export default SuperCutGridCell
